import { generatePath, useLocation } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { CustomerSection } from "@acto/taskpane/field-sales-2/components/patterns/CustomerSection";
import { RecapEntryBanner } from "@acto/taskpane/field-sales-2/components/patterns/recap/RecapEntryBanner";
import { OverdueTodosBanner } from "@acto/taskpane/field-sales-2/components/patterns/todo/OverdueTodosBanner";
import { HomeGreetingCard } from "@acto/taskpane/field-sales-2/components/patterns/HomeGreetingCard";
import { HomeMeetingCalendar } from "@acto/taskpane/field-sales-2/components/patterns/HomeMeetingCalendar";
import { RecognizedCustomersSection } from "@acto/taskpane/field-sales-2/components/patterns/RecognizedCustomers";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import { useContributeChatScope, customersToClients } from "@acto/taskpane/core/chat-envelope";
import { CLIENT_LIST_LIMIT, INCLUDE_CLIENT_DETAILS } from "@acto/taskpane/field-sales-2/utils/chatScope";
import { AnalyticsEvents, AnalyticsSourceScope, useTrack } from "@acto/taskpane/core/analytics";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { ScrollPage, ScrollPageTitle } from "@acto/taskpane/field-sales-2/components/primitives/ScrollPage";
import { PeriodComparisonScope } from "@acto/taskpane/field-sales-2/components/patterns/PeriodComparisonScope/PeriodComparisonScope";
import { YEAR_TO_DATE_SELECTION } from "@acto/taskpane/field-sales-2/utils/periodSelection";
import { useHomeCustomerList } from "./useHomeCustomerList";
import { type QuickFilterState } from "./quickFilter";

const FOCUS_COUNT = 3;

// Restrict the home list to customers with an observation — the "im Fokus" list.
const FOCUS_QUICK_FILTER: QuickFilterState = { focus: true, growth: false, decline: false };

// Inset to the page gutter; the flex `gap` collapses when only one pill renders, so no
// dangling space.
const NudgeSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
  padding: `0 ${theme.fieldSalesTwo.spacing[12]}`,
}));

export function Home() {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const navigate = useDirectionalNavigate();
  const location = useLocation();
  const track = useTrack();

  // "Kunden im Fokus" = only customers that have an observation (server-side focusOnly),
  // paginated (load 3, then more on scroll) — same list and logic as the Calendar page.
  const { customers, revenuePeriod, isLoading, isLoadingMore, hasMore, loadMore } = useHomeCustomerList({
    search: "",
    quickFilter: FOCUS_QUICK_FILTER,
    pageSize: FOCUS_COUNT,
  });

  // Ambient context: contribute the rendered client list to the home scope.
  const clientsFragment = customersToClients(customers, {
    includeDetails: INCLUDE_CLIENT_DETAILS,
    locale,
    revenuePeriod,
    limit: CLIENT_LIST_LIMIT,
  });
  useContributeChatScope("home", clientsFragment);

  const handleOpenCustomer = (customer: Customer) => {
    track(AnalyticsEvents.RECOMMENDED_CUSTOMER_CLICKED, {
      customerId: customer.id,
      customerName: customer.name ?? "",
      customerExternalReference: customer.externalReference,
      source: "home",
    });
    navigate(generatePath(ActoRoutes.CUSTOMER_DETAILS, { id: encodeURIComponent(customer.id) }), {
      state: { from: location.pathname },
    });
  };

  return (
    <AnalyticsSourceScope source="home">
      <PeriodComparisonScope selection={YEAR_TO_DATE_SELECTION}>
        <ScrollPage>
          <HomeGreetingCard />
          <NudgeSection>
            <RecapEntryBanner />
            <OverdueTodosBanner />
          </NudgeSection>
          <HomeMeetingCalendar />
          <RecognizedCustomersSection />
          <ScrollPageTitle>{t("home.focusTitle")}</ScrollPageTitle>
          <CustomerSection
            revenuePeriod={revenuePeriod}
            customers={customers}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
            loadMore={loadMore}
            skeletonCount={FOCUS_COUNT}
            onOpenCustomer={handleOpenCustomer}
          />
        </ScrollPage>
      </PeriodComparisonScope>
    </AnalyticsSourceScope>
  );
}
